// Keyboard shortcut registry for the Shortcuts dialog: global shortcuts plus the per-feature lists (Requirements, Source, Library, Parameters and Glossary, Curves, Assistant, Assessment).
import { ASSESSMENT_SHORTCUTS } from "@/features/assessments/assessment/shortcuts";
import { ASSISTANT_SHORTCUTS } from "@/features/assistant/shortcuts";
import { CURVE_SHORTCUTS } from "@/features/curves/shortcuts";
import { LIBRARY_SHORTCUTS } from "@/features/library/shortcuts";
import { SOURCE_SHORTCUTS } from "@/features/source/shortcuts";
import { VOCABULARY_SHORTCUTS } from "@/features/vocabulary/shortcuts";
import { WORKSPACE_SHORTCUTS } from "@/features/workspace/shortcuts";

export interface ShortcutDef {
  id: string;
  keys: readonly (readonly string[])[];
  description: string;
}

export interface ShortcutSection {
  scope: string;
  shortcuts: readonly ShortcutDef[];
}

export const MOD_KEY_LABEL =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/u.test(navigator.platform) ? "⌘" : "Ctrl";

export const GO_TO_SEQUENCES: readonly { key: string; label: string; navId: string }[] = [
  { key: "o", label: "Overview", navId: "overview" },
  { key: "s", label: "Assessments", navId: "assessments" },
  { key: "a", label: "Assistant", navId: "assistant" },
  { key: "w", label: "Requirements", navId: "workspace" },
  { key: "l", label: "Library", navId: "library" },
  { key: "c", label: "Curves", navId: "curves" },
  { key: "y", label: "Glossary", navId: "glossary" },
  { key: "v", label: "What changed", navId: "versions" },
  { key: "q", label: "Quality", navId: "quality" },
  { key: "p", label: "Parameters", navId: "parameters" },
  { key: "r", label: "Runs & jobs", navId: "runs" },
  { key: "d", label: "Datasets", navId: "datasets" },
];

export const GLOBAL_SHORTCUTS: readonly ShortcutDef[] = [
  { id: "palette", keys: [[MOD_KEY_LABEL, "K"]], description: "Search and commands" },
  { id: "shortcuts", keys: [["?"]], description: "Keyboard shortcuts" },
  { id: "escape", keys: [["Esc"]], description: "Close dialogs, sheets and popovers" },
  { id: "sidebar", keys: [["["]], description: "Collapse or expand the navigation" },
  ...GO_TO_SEQUENCES.map((sequence) => ({
    id: `go-${sequence.navId}`,
    keys: [["G", sequence.key.toUpperCase()]],
    description: `Go to ${sequence.label}`,
  })),
];

export const SHORTCUT_SECTIONS: readonly ShortcutSection[] = [
  { scope: "Everywhere", shortcuts: GLOBAL_SHORTCUTS },
  { scope: "Requirements", shortcuts: WORKSPACE_SHORTCUTS },
  { scope: "Source viewer", shortcuts: SOURCE_SHORTCUTS },
  { scope: "Library", shortcuts: LIBRARY_SHORTCUTS },
  { scope: "Parameters and glossary", shortcuts: VOCABULARY_SHORTCUTS },
  { scope: "Curve chart", shortcuts: CURVE_SHORTCUTS },
  { scope: "Assistant", shortcuts: ASSISTANT_SHORTCUTS },
  { scope: "Assessment", shortcuts: ASSESSMENT_SHORTCUTS },
].filter((section) => section.shortcuts.length > 0);

export const SEQUENCE_TIMEOUT_MS = 1000;

let goSequenceStartedAt = 0;

export function startGoSequence(): void {
  goSequenceStartedAt = Date.now();
}

export function goSequencePending(): boolean {
  return Date.now() - goSequenceStartedAt < SEQUENCE_TIMEOUT_MS;
}

export function consumeGoSequence(): boolean {
  const pending = goSequencePending();
  goSequenceStartedAt = 0;
  return pending;
}

const FORM_SELECTOR =
  "input, textarea, select, [contenteditable=''], [contenteditable='true'], [role='combobox']";

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  return target.closest(FORM_SELECTOR) !== null;
}

export function isDialogOpen(): boolean {
  return (
    document.querySelector("[role='dialog'][data-state='open'], [role='alertdialog'][data-state='open']") !==
    null
  );
}

export function shouldIgnoreSingleKey(event: KeyboardEvent): boolean {
  return isTypingTarget(event.target) || isDialogOpen();
}
